import { MailOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeletedFilled } from 'assets/images/svg';
import { forbiddenDomains } from 'config/forbiddenDomains';
import { invitedStatus } from 'config/invitedStatus';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople, selectSelectedUsers } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { Alert, Avatar, Button, Col, Empty, Flex, Form, Input, List, Row, Tag, Typography } from 'zui/Antd';

const { Text } = Typography;

const LockersAddPeopleForm = () => {
	//✅ Hooks
	const {
		token: {
			fontSizeSM,
			paddingXS,
			paddingSM,
			colorText,
			fontWeightStrong,
			colorTextLabel,
			colorIcon,
			colorBorder,
			borderMD = 6,
			paddingXXS,
			paddingXL,
			colorTextPlaceholder,
			colorPrimaryBg,
			colorPrimary
		}
	} = theme.useToken();
	const [form] = Form.useForm();
	const dispatch = useDispatch();

	//✅ Redux state
	const people = useSelector(selectPeople);
	const selectedUsers = useSelector(selectSelectedUsers);

	//✅ Local state
	const [alert, setAlert] = React.useState({ message: '', type: '' });

	//✅ Function to add users by email
	const handleAddUserByEmail = async () => {
		try {
			const values = await form.validateFields();
			const { email } = values;

			const emails = email.split(',').map((e) => e.trim());
			const validEmails = [...new Set(emails)];

			const errors = [];
			let hasNewEmails = false;

			for (const e of validEmails) {
				const domain = e.split('@')[1]?.toLowerCase();

				// validate email not include prohibited domains
				if (forbiddenDomains.includes(domain)) {
					errors.push(`Domain not allowed: ${domain}`);
					continue;
				}

				// validate emails not include existing people emails
				const existsInPeople = people.find((person) => person.email === e);
				if (existsInPeople) {
					errors.push(`Already exists in organisation: ${e}`);
					continue;
				}

				// Check if email already exists in selected users
				const existsInSelected = selectedUsers.find((user) => user.email === e);
				if (existsInSelected) {
					errors.push(`Already in the list: ${e}`);
					continue;
				}

				// Add email to Redux store
				const newUser = {
					personID: Date.now(),
					email: e,
					status: invitedStatus.PENDING,
					type: 'email'
				};

				dispatch(peopleActions.addSelectedUser(newUser));
				hasNewEmails = true;
			}

			// ⚠️ Show alert if there are validation errors
			if (errors.length > 0) {
				setAlert({
					message: errors.join('<br/>')
				});
			} else if (hasNewEmails) {
				setAlert({ message: '', type: '' });
			}

			form.resetFields();
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	//✅ Function to clear the list
	const handleClearList = () => {
		dispatch(peopleActions.clearSelectedUsers());
	};

	//✅ Function to delete a specific user
	const handleDeleteUser = (personID) => {
		dispatch(peopleActions.removeSelectedUser({ personID }));
	};

	return (
		<Form form={form} layout='vertical'>
			<Row gutter={[paddingXS, 0]}>
				<Col span={24}>
					<Form.Item
						name='email'
						label={<Text strong>Invite Email Addresses</Text>}
						rules={fieldsValidator.MultiEmail}
						style={{ marginBottom: paddingXL }}
					>
						<Flex gap={paddingXS}>
							<Input
								placeholder='Enter multiple emails separated by commas or semicolons'
								prefix={<MailOutlined style={{ color: colorIcon }} />}
								onPressEnter={handleAddUserByEmail}
								onChange={() => setAlert({ message: '', type: '' })} // Clear alert on input change
							/>
							<Button type='primary' onClick={handleAddUserByEmail}>
								Add All
							</Button>
						</Flex>
					</Form.Item>
				</Col>

				<Col span={24}>
					{alert.message && (
						<Alert
							message={<div dangerouslySetInnerHTML={{ __html: alert.message }} />}
							type={'warning'}
							showIcon
							closable
							onClose={() => setAlert({ message: '', type: '' })}
							style={{ marginBottom: paddingXS, fontSize: fontSizeSM }}
						/>
					)}
				</Col>

				{/* List of added users */}
				<Col span={24}>
					<Flex gap={paddingSM} vertical>
						<Flex align='center' justify='space-between'>
							<Text strong>Selected Email Addresses</Text>
							<Button type='text' onClick={handleClearList} style={{ textDecoration: 'underline' }}>
								Clear List
							</Button>
						</Flex>

						<Flex
							style={{
								height: 145,
								overflowY: 'auto',
								padding: `${paddingSM}px 0px`,
								borderRadius: borderMD,
								border: `1px solid ${colorBorder}`
							}}
						>
							{selectedUsers && selectedUsers.length > 0 ? (
								<List
									dataSource={selectedUsers}
									split={false}
									style={{ width: '100%', padding: `0px ${paddingXS}px` }}
									renderItem={(user, index) => {
										// Show background color only when user is being processed (INVITING), not when completed (INVITED)
										const isBeingProcessed = user.status === invitedStatus.INVITING;

										return (
											<List.Item
												style={{
													padding: `${paddingXXS}px ${paddingSM}px`,
													width: '100%',
													borderBottom: 'none',
													marginBottom: index < selectedUsers.length - 1 ? paddingXXS : 0,
													backgroundColor: isBeingProcessed ? '#E3F2FD' : 'transparent',
													borderRadius: borderMD
												}}
												extra={
													<Flex align='center' gap={paddingXXS}>
														{/* Show status tag only when user is being processed or completed */}
														{user.status === invitedStatus.INVITING && (
															<Tag
																color={colorPrimaryBg}
																bordered={false}
																style={{
																	fontWeight: fontWeightStrong,
																	color: colorPrimary
																}}
															>
																Inviting
															</Tag>
														)}

														{user.status === invitedStatus.INVITED && (
															<Tag
																color='green'
																bordered={false}
																style={{ fontWeight: fontWeightStrong }}
															>
																Invited
															</Tag>
														)}

														{/* Show delete button only for pending users */}
														{user.status === invitedStatus.PENDING && (
															<Button
																icon={<DeletedFilled width={16} height={16} />}
																onClick={() => handleDeleteUser(user.personID)}
																type='text'
																style={{ color: colorTextLabel }}
															/>
														)}
													</Flex>
												}
											>
												<List.Item.Meta
													avatar={
														<Avatar
															size={30}
															style={{
																fontSize: fontSizeSM,
																color: colorText,
																fontWeight: fontWeightStrong,
																backgroundColor: '#D9D9D9'
															}}
														>
															{user.email?.charAt(0).toUpperCase()}
														</Avatar>
													}
													title={<Text style={{ fontWeight: 400 }}>{user.email}</Text>}
												/>
											</List.Item>
										);
									}}
								/>
							) : (
								// tried to use locale property of List but it was not working properly.
								<Flex
									align='center'
									justify='center'
									style={{
										width: '100%'
									}}
								>
									<Empty
										style={{ color: colorTextPlaceholder }}
										description='Add email addresses to this list to invite them to the platform'
										image={Empty.PRESENTED_IMAGE_SIMPLE}
									/>
								</Flex>
							)}
						</Flex>
					</Flex>
				</Col>
			</Row>
		</Form>
	);
};

export default LockersAddPeopleForm;
